import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { useCostCalculator } from '../hooks/useCostCalculator';
import { LIPS_MODELS } from '../constants/models';
import Icons from '../components/Icons';
import Lightbox from '../components/Lightbox';
import MediaTrimmer from '../components/MediaTrimmer';
import * as api from '../services/api';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import { compressImage } from '../utils/imageCompression';
import { downloadFile } from '../utils/downloadFile';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { confirmAlert } from '../utils/confirmAlert';

export default function LipSyncPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, items, refreshItems, removeItem, pendingJobs, setPendingJobs } = useCredits();
  const navigate = useNavigate();

  const [model, setModel] = useState('meigen-ai-infinitetalk');
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cd, setCd] = useState(0);
  const cdRef = useRef();
  const genLockRef = useRef(false);

  const [prompt, setPrompt] = useState('');
  const [sourceFile, setSourceFile] = useState(null);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [sourceTrim, setSourceTrim] = useState({ start: 0, end: 15 });
  const [audioTrim, setAudioTrim] = useState({ start: 0, end: 0 });
  const [showAudioTrimmer, setShowAudioTrimmer] = useState(false);
  const [tempFile, setTempFile] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [libTarget, setLibTarget] = useState(null); // 'image' or 'audio'
  const [mode, setMode] = useState('720p');
  const [resultUrl, setResultUrl] = useState(null);
  const [lbItem, setLbItem] = useState(null);

  const sourceRef = useRef();
  const audioRef = useRef();
  const currentModel = LIPS_MODELS.find(m => m.id === model);
  // All current lip-sync models support max 15s
  const maxDur = 15;

  React.useEffect(() => {
    if (model === 'wan-2.2' && !['480p 30 fps', '580p 30 fps', '720p 30 fps', '480p 60 fps', '580p 60 fps', '720p 60 fps'].includes(mode)) setMode('480p 30 fps');
    else if (model === 'meigen-ai-infinitetalk' && !['480p', '720p'].includes(mode)) setMode('720p');
    else if (model === 'kling-avatar' && !['standard', 'pro'].includes(mode)) setMode('standard');

    // Only show trim toast if user has actually uploaded an audio file
    if (audioFile) {
      setAudioTrim(p => {
        if (p.end - p.start > maxDur) {
          toast(`Trim shortened to ${maxDur}s limit`, { icon: '✂️' });
          return { ...p, end: p.start + maxDur };
        }
        return p;
      });
    }
  }, [model, maxDur, mode, audioFile]);

  const cost = useCostCalculator({
    mode: 'lip-sync',
    lipSyncModel: model,
    lipSyncMode: mode,
    duration: audioFile ? (audioTrim.end - audioTrim.start) : 0,
  });

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  const handleSourceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (sourcePreviewUrl) URL.revokeObjectURL(sourcePreviewUrl);
    const compressed = await compressImage(file);
    setSourceFile(compressed);
    setSourcePreviewUrl(URL.createObjectURL(compressed));
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAudioTrim({ start: 0, end: maxDur });
    setTempFile(file);
    setShowAudioTrimmer(true);
  };

  const handleGen = useCallback(() => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (!sourceFile) { toast.error('Upload a character image'); return; }
    if (!audioFile) { toast.error('Upload an audio track'); return; }
    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }
    if (loading) { toast.error('Generation already in progress'); return; }

    genLockRef.current = true;

    const jobId = `lspnd_${Date.now()}`;
    setPendingJobs(prev => [{ id: jobId, prompt: prompt || 'Lip-Sync', model: currentModel?.label || model, type: 'lip-sync', thumb: sourcePreviewUrl }, ...prev]);
    setLoading(true);
    setResultUrl(null);
    startCD(15);
    toast.success('Lip-Sync generation started — this may take 1–5 minutes');

    // Non-blocking: fire in background, do NOT await
    (async () => {
      try {
        const res = await api.generateLipSync(token, {
          model,
          sourceVideo: sourceFile?.fileUrl || sourceFile,
          audioFile: audioFile?.fileUrl || audioFile,
          mode,
          prompt,
          sourceTrimStart: sourceTrim.start,
          sourceTrimEnd: sourceTrim.end,
          audioTrimStart: audioTrim.start,
          audioTrimEnd: audioTrim.end,
        });
        if (res.success) {
          setResultUrl(res.videoUrl);
          setCredits(res.creditsRemaining);
          toast.success('Lip-Sync complete! 🎉');
          refreshItems(true);
        }
      } catch (e) {
        const errMsg = e.response?.data?.error || e.message || 'Generation failed';
        toast.error(errMsg);
        if (e.response?.data?.creditsRemaining !== undefined) {
          setCredits(e.response.data.creditsRemaining);
        }
      } finally {
        setPendingJobs(prev => prev.filter(j => j.id !== jobId));
        genLockRef.current = false;
        setLoading(false);
      }
    })();
  }, [user, cd, sourceFile, audioFile, credits, cost, navigate, onShowAuth, refreshItems, token, model, mode, prompt, sourceTrim, audioTrim, currentModel, loading, sourcePreviewUrl]);

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try { await api.deleteGeneration(token, id); removeItem(id); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const recentVideos = (items || [])
    .filter(i => i.subType === 'lip-sync' || i.type === 'lip-sync')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    }).slice(0, 12);

  const localJobs = pendingJobs.filter(j => j.type === 'lip-sync');

  return (
    <>
      {showAudioTrimmer && <MediaTrimmer file={tempFile} type="audio" maxDuration={maxDur}
        onSave={(s, e) => {
          setAudioTrim({ start: s, end: e });
          setAudioFile(tempFile);
          if (audioPreviewUrl && audioPreviewUrl.startsWith('blob:')) URL.revokeObjectURL(audioPreviewUrl.split('#')[0]);
          
          let baseObjUrl = '';
          if (tempFile instanceof Blob || tempFile instanceof File) {
             baseObjUrl = URL.createObjectURL(tempFile);
          } else {
             baseObjUrl = tempFile.fileUrl || tempFile.url;
          }

          setAudioPreviewUrl(`${baseObjUrl}#t=${s.toFixed(2)},${e.toFixed(2)}`);
          setShowAudioTrimmer(false);
          setTempFile(null);
        }}
        onCancel={() => { setShowAudioTrimmer(false); setTempFile(null); }} />}

      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Lip-Sync Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}><Icons.X sz={14} /></button>
            </div>
            <div className="mp-body">
              {LIPS_MODELS.map(m => (
                <button key={m.id} className={`mp-item ${model === m.id ? 'on' : ''}`}
                  onClick={() => { setModel(m.id); setShowModelPicker(false); }}>
                  {m.icon && Icons[m.icon] ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                      {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                    </div>
                  ) : <div className="mp-dot" style={{ background: m.color }} />}
                  <div className="mp-info">
                    <div className="mp-name">{m.label}</div>
                  </div>
                  {model === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.LipSync sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      {/* ── Settings Panel ── */}
      <section className="panel">
        <div className="phead"><Icons.Vid sz={16} /> Lip-Sync</div>

        {/* Model */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 10, color: currentModel?.color }}>
                {currentModel?.icon && Icons[currentModel.icon] ? (
                  React.createElement(Icons[currentModel.icon], { sz: 20, color: currentModel.color })
                ) : <div className="mp-dot" style={{ background: currentModel?.color }} />}
              </div>
              <div>
                <div className="mp-trigger-name">{currentModel?.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </button>
        </div>

        {/* Character Source Upload */}
        <div className="psec">
          <div className="slbl">Character Image</div>
          <div className={`uzone ${sourceFile ? 'on' : ''}`} onClick={() => { setLibTarget('image'); setLibOpen(true); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) handleSourceUpload({ target: { files: e.dataTransfer.files } });
            }}>
            {sourceFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  {sourcePreviewUrl ? (
                    <img src={sourcePreviewUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--s3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}>
                      <Icons.ImgFrame sz={22} />
                    </div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setSourceFile(null); setSourcePreviewUrl(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>✕</button>
                </div>
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: 'var(--t2)', fontSize: 12, fontWeight: 700 }}>{sourceFile.name || 'Library Image'}</span>
                </div>
              </div>
            ) : (
              <><div style={{ opacity: .4 }}><Icons.Upload /></div><div>Visual References</div></>
            )}
          </div>
        </div>

        {/* Audio Upload */}
        <div className="psec">
          <div className="slbl">Voice / Audio <span className="opt">— Max {maxDur}s</span></div>
          <div className={`uzone ${audioFile ? 'on' : ''}`} onClick={() => { setLibTarget('audio'); setLibOpen(true); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) handleAudioUpload({ target: { files: e.dataTransfer.files } });
            }}>
            {audioFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--s3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}>
                    <Icons.Waveform sz={22} />
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setAudioFile(null); setAudioPreviewUrl(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>✕</button>
                </div>
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: 'var(--t2)', fontSize: 12, fontWeight: 700 }}>{audioFile.name || 'Library Audio'}</span>
                  {audioFile.fileUrl ? (
                    <span style={{ color: 'var(--t3)', fontSize: 10 }}>Entire track (trimming unsupported for library files)</span>
                  ) : (
                    <span style={{ color: 'var(--t3)', fontSize: 10 }}>Trim: {audioTrim.start.toFixed(1)}s – {audioTrim.end.toFixed(1)}s</span>
                  )}
                </div>
              </div>
            ) : (
              <><div style={{ opacity: .4 }}><Icons.Upload /></div><div>Audio References</div></>
            )}
          </div>
          {audioFile && !audioFile.fileUrl && (
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--or)', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => { setTempFile(audioFile); setShowAudioTrimmer(true); }}>
                ✂️ Adjust Trim
              </span>
            </div>
          )}
        </div>

        {/* Prompt */}
        <div className="psec">
          <div className="slbl">Prompt <span className="opt">— optional</span></div>
          <div className="pwrap">
            <textarea className="ptxt" style={{ minHeight: 55 }}
              placeholder="Describe any scene styling or character emotion..."
              value={prompt} onChange={e => setPrompt(e.target.value)} maxLength={2500} />
            <div className="pbot">
              <span className="pcnt">{prompt.length}/2500</span>
            </div>
          </div>
        </div>

        {/* Mode Selector */}
        <div className="psec">
          <div className="slbl">Resolution &amp; Mode</div>
          <div className="pills" style={{ flexWrap: 'wrap', gap: 8 }}>
            {model === 'wan-2.2' && ['480p 30 fps', '580p 30 fps', '720p 30 fps', '480p 60 fps', '580p 60 fps', '720p 60 fps'].map(opt => (
              <button key={opt} className={`pill ${mode === opt ? 'on' : ''}`} onClick={() => setMode(opt)}>{opt}</button>
            ))}
            {model === 'meigen-ai-infinitetalk' && (
              <>
                <button className={`pill ${mode === '480p' ? 'on' : ''}`} onClick={() => setMode('480p')}>
                  480p <span style={{ opacity: 0.6, fontSize: 9 }}>(25 cr/s)</span>
                </button>
                <button className={`pill ${mode === '720p' ? 'on' : ''}`} onClick={() => setMode('720p')}>
                  720p <span style={{ opacity: 0.6, fontSize: 9 }}>(40 cr/s)</span>
                </button>
              </>
            )}
            {model === 'kling-avatar' && (
              <>
                <button className={`pill ${mode === 'standard' ? 'on' : ''}`} onClick={() => setMode('standard')}>
                  Standard <span style={{ opacity: 0.6, fontSize: 9 }}>(720p — 30 cr/s)</span>
                </button>
                <button className={`pill ${mode === 'pro' ? 'on' : ''}`} onClick={() => setMode('pro')}>
                  Pro <span style={{ opacity: 0.6, fontSize: 9 }}>(1080p — 40 cr/s)</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Generate */}
        <div className="gen-sticky">
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">{audioFile ? `${cost} cr` : '—'} {audioFile && <> {' → '} {Math.max(0, (credits || 0) - cost)} left</>}</span>
          </div>
          <button className="gbtn" onClick={handleGen} disabled={loading || (user && credits < cost) || cd > 0}>
            {cd > 0 ? `Wait ${cd}s` : !user ? <>✨ Sign up to Generate</> : <><Icons.Vid sz={15} /> Generate Lip-Sync</>}
          </button>
          {user && credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }} onClick={() => navigate('/subscription')}>Insufficient credits — <u>upgrade</u></p>
          )}
        </div>
      </section>

      {/* ── Main Canvas ── */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">Preview</div>
          <div style={{ flex: 1 }} />
          <div className="mtab" onClick={() => navigate('/gallery')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Grid sz={13} /> Creations
          </div>
        </div>
        <div className="mbody">
          <div className="prev">
            {resultUrl ? (
              <div className="prev-wrap">
                <div className="prev-toolbar">
                  <div className="prev-tb-right" style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <button className="prev-icon-btn" onClick={() => downloadFile(resultUrl, token, true).catch(() => toast.error('Download failed'))} title="Download">
                      <Icons.DL sz={15} />
                    </button>
                    <button className="prev-icon-btn del" onClick={async () => {
                      const isConfirmed = await confirmAlert({
                        title: 'Clear Result?',
                        message: 'Are you sure you want to clear this result?'
                      });
                      if (isConfirmed) setResultUrl(null);
                    }} title="Clear">
                      <Icons.Trash sz={15} />
                    </button>
                  </div>
                </div>
                <video src={resultUrl} controls autoPlay loop style={{ width: '100%', borderRadius: 'var(--r3)', background: '#000' }} />
              </div>

            ) : loading ? (
              /* ── Generating State: shows character image + progress bar ── */
              <div className="prev-empty" style={{ position: 'relative' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                  {/* Character image with spinner ring around it */}
                  <div style={{ position: 'relative', width: 100, height: 100 }}>
                    <div className="spinner" style={{ position: 'absolute', top: -6, left: -6, width: 112, height: 112, borderWidth: 3 }} />
                    {sourcePreviewUrl ? (
                      <img src={sourcePreviewUrl} alt="Character"
                        style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--b2)' }} />
                    ) : (
                      <div style={{ width: 100, height: 100, borderRadius: '50%', background: 'var(--b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
                        🎙️
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t2)' }}>Generating Lip-Sync…</div>
                  <div style={{ fontSize: 11, color: 'var(--t3)', textAlign: 'center', maxWidth: 260 }}>
                    Model: <strong style={{ color: currentModel?.color }}>{currentModel?.label}</strong>
                    {' · '}{mode}<br />
                    This may take 1–5 minutes. Please wait.
                  </div>
                  <div className="pend-bar-wrap" style={{ width: 200, marginTop: 4 }}><div className="pend-bar" /></div>
                </div>
              </div>

            ) : sourcePreviewUrl || audioPreviewUrl ? (
              <div className="prev-wrap" style={{ display: 'flex', flexDirection: 'row', gap: 12, height: 240, alignItems: 'center', justifyContent: 'center', padding: 20, width: '100%', maxWidth: 800 }}>
                {sourcePreviewUrl && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 6, fontWeight: 700 }}>CHARACTER</div>
                    <img src={sourcePreviewUrl} alt="Character"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, background: 'rgba(0,0,0,0.2)', minHeight: 0 }} />
                  </div>
                )}
                {audioPreviewUrl && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 6, fontWeight: 700 }}>
                      AUDIO ({audioTrim.start.toFixed(1)}s – {audioTrim.end.toFixed(1)}s)
                    </div>
                    <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 20 }}>
                      <audio key={audioPreviewUrl} src={audioPreviewUrl} controls style={{ width: '100%' }} />
                    </div>
                  </div>
                )}
              </div>

            ) : (
              <div className="prev-empty">
                <div className="ico"><Icons.Vid sz={36} /></div>
                <div style={{ fontSize: 13 }}>Lip-synced video will appear here</div>
                <div style={{ fontSize: 11, color: 'var(--t3)' }}>
                  Model: <strong style={{ color: currentModel?.color }}>{currentModel?.label}</strong>
                  {' · '}{mode}
                </div>
              </div>
            )}
          </div>

          {/* ── Pending + Recent Grid ── */}
          {(localJobs.length > 0 || recentVideos.length > 0) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Grid sz={13} /> {localJobs.length > 0 ? 'Generating…' : 'Recent Lip-Sync'}
              </div>
              <div className="ggrid">
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending">
                    {/* Show character image thumbnail if available */}
                    {j.thumb ? (
                      <img src={j.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--r2)', opacity: 0.5, position: 'absolute', top: 0, left: 0 }} />
                    ) : null}
                    <div className="pend-icon" style={{ position: 'relative', zIndex: 1 }}><Icons.LipSync sz={28} /></div>
                    <div className="pend-label" style={{ position: 'relative', zIndex: 1 }}>{j.prompt || 'Generating…'}</div>
                    <div className="pend-model" style={{ position: 'relative', zIndex: 1 }}>{j.model}</div>
                    <div className="pend-bar-wrap" style={{ position: 'relative', zIndex: 1 }}><div className="pend-bar" /></div>
                  </div>
                ))}
              </div>

              <div className="grouped-collections">
                {groupItemsByDate(recentVideos).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" onClick={() => setLbItem(i)}>
                          {i.videoUrl ? (
                            <video src={i.videoUrl} muted loop playsInline
                              onMouseEnter={e => e.target.play()}
                              onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <img src={i.imageUrl} alt="" />
                          )}
                          <div className="gbadge" style={{ background: 'rgba(236,72,153,.12)', color: '#ec4899' }}>
                            <Icons.Vid sz={11} />
                          </div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if(typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt || 'Lip-Sync'}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />
      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={libTarget === 'audio' ? ['audio'] : ['image']}
        maxSelect={1}
        maxTypeCounts={libTarget === 'audio' ? { audio: 1, image: 0, video: 0 } : { image: 1, audio: 0, video: 0 }}
        onSelect={(assets) => {
          if (libTarget === 'audio') {
            setTempFile(assets[0]);
            setShowAudioTrimmer(true);
          } else {
            setSourceFile(assets[0]);
            setSourcePreviewUrl(assets[0].fileUrl);
          }
        }}
      />
    </>
  );
}


